import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Edit, Eye, ArrowRight, MessageSquare, Download, Phone, Mail, Calendar, ExternalLink, Users, Zap, Settings, HelpCircle, Star, Shield } from "lucide-react";
import type { CTAButton } from "@/hooks/useCTAButtons";

const ICON_OPTIONS = [
  { name: "ArrowRight", icon: ArrowRight },
  { name: "MessageSquare", icon: MessageSquare },
  { name: "Download", icon: Download },
  { name: "Phone", icon: Phone },
  { name: "Mail", icon: Mail },
  { name: "Calendar", icon: Calendar },
  { name: "ExternalLink", icon: ExternalLink },
  { name: "Users", icon: Users },
  { name: "Zap", icon: Zap },
  { name: "Star", icon: Star },
  { name: "Shield", icon: Shield },
  { name: "HelpCircle", icon: HelpCircle },
];

export const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = Object.fromEntries(
  ICON_OPTIONS.map((o) => [o.name, o.icon])
);

function getIcon(name: string) {
  return ICON_MAP[name] || ArrowRight;
}

interface FormState {
  id?: string;
  key: string;
  label: string;
  icon_name: string;
  tanya_greeting: string;
  tanya_context_hint: string;
  active: boolean;
  sort_order: number;
  form_id: string | null;
}

const emptyForm: FormState = {
  key: "", label: "", icon_name: "ArrowRight",
  tanya_greeting: "", tanya_context_hint: "",
  active: true, sort_order: 0, form_id: null,
};

export default function CTAButtonsAdmin({ readOnly = false }: { readOnly?: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<FormState | null>(null);
  const [deleting, setDeleting] = useState<CTAButton | null>(null);

  const { data: buttons, isLoading } = useQuery({
    queryKey: ["cta-buttons-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cta_buttons")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return data as (CTAButton & { form_id?: string | null })[];
    },
  });

  // Fetch chat-mode forms for the form picker
  const { data: chatForms } = useQuery({
    queryKey: ["chat-forms-for-cta"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_forms" as any)
        .select("id, name, form_type, heading")
        .eq("display_mode", "chat")
        .eq("active", true)
        .order("name");
      if (error) throw error;
      return (data ?? []) as unknown as { id: string; name: string; form_type: string; heading: string | null }[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (form: FormState) => {
      if (form.id) {
        const { error } = await supabase.from("cta_buttons").update({
          key: form.key, label: form.label, icon_name: form.icon_name,
          tanya_greeting: form.tanya_greeting, tanya_context_hint: form.tanya_context_hint,
          active: form.active, sort_order: form.sort_order, form_id: form.form_id || null,
        } as any).eq("id", form.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("cta_buttons").insert({
          key: form.key, label: form.label, icon_name: form.icon_name,
          tanya_greeting: form.tanya_greeting, tanya_context_hint: form.tanya_context_hint,
          active: form.active, sort_order: form.sort_order, form_id: form.form_id || null,
        } as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cta-buttons-all"] });
      qc.invalidateQueries({ queryKey: ["cta-buttons"] });
      toast.success("CTA button saved");
      setEditing(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("cta_buttons").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cta-buttons-all"] });
      qc.invalidateQueries({ queryKey: ["cta-buttons"] });
      toast.success("CTA button deleted");
      setDeleting(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const slugify = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-background/95 backdrop-blur border-b flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold">CTA Buttons</h2>
          <p className="text-sm text-muted-foreground">Manage call-to-action buttons used across the website. Each button triggers a specific Tanya conversation flow.</p>
        </div>
        {!readOnly && (
          <Button className="shrink-0" onClick={() => setEditing({ ...emptyForm, sort_order: (buttons?.length ?? 0) + 1 })}>
            <Plus className="w-4 h-4 mr-1" /> Add CTA
          </Button>
        )}
      </div>

      <div className="grid gap-2">
        {(buttons ?? []).map((cta) => {
          const Icon = getIcon(cta.icon_name);
          return (
            <Card key={cta.id} className={`${!cta.active ? "opacity-50" : ""}`}>
              <CardContent className="flex items-center gap-3 p-3">
                <div className="flex items-center justify-center w-9 h-9 rounded-md bg-primary/10 text-primary shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-medium text-sm">{cta.label}</span>
                    <code className="text-[11px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{cta.key}</code>
                    {!cta.active && <span className="text-[11px] text-destructive font-medium">Inactive</span>}
                    {(cta as any).form_id && <span className="text-[11px] text-accent font-medium">Form linked</span>}
                  </div>
                  {cta.tanya_context_hint && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 break-words" title={cta.tanya_context_hint}>
                      {cta.tanya_context_hint}
                    </p>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing({
                    id: cta.id, key: cta.key, label: cta.label, icon_name: cta.icon_name,
                    tanya_greeting: cta.tanya_greeting, tanya_context_hint: cta.tanya_context_hint,
                    active: cta.active, sort_order: cta.sort_order, form_id: (cta as any).form_id || null,
                  })}>
                    {readOnly ? <Eye className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                  </Button>
                  {!readOnly && (
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDeleting(cta)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit CTA Button" : "New CTA Button"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div>
                <Label>Label</Label>
                <Input value={editing.label} onChange={(e) => {
                  const label = e.target.value;
                  setEditing({ ...editing, label, ...(editing.id ? {} : { key: slugify(label) }) });
                }} placeholder="e.g. Request a Demo" />
              </div>
              <div>
                <Label>Key (slug)</Label>
                <Input value={editing.key} onChange={(e) => setEditing({ ...editing, key: e.target.value })}
                  placeholder="e.g. request-demo" className="font-mono text-sm" />
                <p className="text-xs text-muted-foreground mt-1">Unique identifier used in code and page settings</p>
              </div>
              <div>
                <Label>Icon</Label>
                <Select value={editing.icon_name} onValueChange={(v) => setEditing({ ...editing, icon_name: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map((opt) => {
                      const I = opt.icon;
                      return (
                        <SelectItem key={opt.name} value={opt.name}>
                          <span className="flex items-center gap-2">
                            <I className="w-4 h-4" /> {opt.name}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tanya Greeting</Label>
                <Textarea value={editing.tanya_greeting} onChange={(e) => setEditing({ ...editing, tanya_greeting: e.target.value })}
                  placeholder="What Tanya says when this CTA is clicked..." rows={4} />
                <p className="text-xs text-muted-foreground mt-1">
                  Tokens: <code className="bg-muted px-1 rounded">{"{name}"}</code> = visitor name,
                  <code className="bg-muted px-1 rounded ml-1">{"{context_line}"}</code> = page context
                </p>
              </div>
              <div>
                <Label>Tanya Context Hint</Label>
                <Textarea value={editing.tanya_context_hint} onChange={(e) => setEditing({ ...editing, tanya_context_hint: e.target.value })}
                  placeholder="System instruction for Tanya's AI..." rows={3} />
                <p className="text-xs text-muted-foreground mt-1">
                  Hidden instruction passed to Tanya's AI to guide the conversation flow
                </p>
              </div>
              <div>
                <Label>Linked Chat Form</Label>
                <Select
                  value={editing.form_id || "none"}
                  onValueChange={(v) => setEditing({ ...editing, form_id: v === "none" ? null : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No form linked" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No form — Tanya handles via conversation</SelectItem>
                    {(chatForms ?? []).map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name} ({f.form_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Optionally link a chat-mode form that Tanya will show inline after the greeting
                </p>
              </div>
              <div>
                <Label>Sort Order</Label>
                <Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })} />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <Label>Active</Label>
              </div>
              <Button className="w-full" onClick={() => saveMutation.mutate(editing)} disabled={saveMutation.isPending || !editing.key || !editing.label}>
                {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Save
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete CTA Button</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleting?.label}"? Pages using this CTA will no longer display it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleting && deleteMutation.mutate(deleting.id)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
